import React from 'react';
import { getColorForValue } from '../../Utils/ColorUtils';

/**
 * EvaluationsOverview Component
 * Displays evaluation overview cards with name, seniority/role, score, and cheating assessment
 */
const EvaluationsOverview = ({ evals, hideCheatingProbability = false }) => {
  if (!evals || !Array.isArray(evals) || evals.length === 0) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Evaluations Overview</h2>
        <div className="text-center text-gray-500 py-8">
          <p>No evaluations available</p>
        </div>
      </div>
    );
  }

  const getCheatingLevel = (probability) => {
    if (probability >= 0.7) return { level: 'HIGH', color: 'text-red-600' };
    if (probability >= 0.4) return { level: 'MEDIUM', color: 'text-yellow-600' };
    return { level: 'LOW', color: 'text-green-600' };
  };

  const formatScore = (score) => {
    if (typeof score === 'string' && score.includes('%')) {
      return score;
    }
    if (typeof score === 'number') {
      // If score is already a percentage (> 1), just add % sign
      if (score > 1) {
        return `${Math.round(score)}%`;
      }
      // If score is a decimal (0-1), convert to percentage
      return `${Math.round(score * 100)}%`;
    }
    return 'N/A';
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Evaluations Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {evals.map((evaluation, index) => {
          // Extract evaluation data
          const evaluationName = evaluation?.evaluations?.name || `Evaluation ${index + 1}`;
          const seniority = evaluation?.evaluations?.evaluation_seniority || '';
          const role = evaluation?.evaluations?.evaluation_role?.subCategory || '';
          
          // Extract score from challenge instance
          const challengeInstance = evaluation?.challenge_instance?.[0];
          const totalScore = challengeInstance?.challenge_final_json?.totalScore;
          const cheatingProbabilityStr = challengeInstance?.challenge_cheating_assessment_json?.cheatingProbability;
          
          // Extract and merge skills from both challenge and questionnaire
          const challengeSkills = challengeInstance?.challenge_final_json?.questions?.[0]?.skillsTested || [];
          const questionnaireSkills = evaluation?.questionnaire_instance?.[0]?.questionnaire_final_json?.questions?.[0]?.inRelationTo || [];
          const skillsTested = [...new Set([...challengeSkills, ...questionnaireSkills])];
          
          const roleText = [seniority, role].filter(Boolean).join(', ') || 'No role specified';
          
          // Convert cheating probability string to number (e.g., "95%" -> 0.95)
          let cheatingProbability = 0;
          if (cheatingProbabilityStr && typeof cheatingProbabilityStr === 'string') {
            cheatingProbability = parseFloat(cheatingProbabilityStr.replace('%', '')) / 100;
          }
          
          const cheatingInfo = getCheatingLevel(cheatingProbability);

          // Convert score to 0-10 scale for color matching
          const scoreValue = typeof totalScore === 'number' 
            ? (totalScore > 1 ? totalScore / 10 : totalScore * 10) 
            : 0;
          const scoreColor = getColorForValue(scoreValue);
          
          // Get light background color based on score
          const getBackgroundForScore = (color) => {
            const backgroundMap = {
              '#EF4444': '#FEF6F6', // much lighter red
              '#F59E0B': '#FEF3C7', // brand yellow light
              '#7FE3D9': '#F5FCFB', // brand green light
              '#33C6B6': '#F5FCFB'  // brand green light
            };
            return backgroundMap[color] || '#F9FAFB';
          };

          return (
            <div 
              key={index} 
              className="rounded-lg p-5"
              style={{
                border: `1px solid ${scoreColor}`,
                backgroundColor: getBackgroundForScore(scoreColor)
              }}
            >
              <div className="flex justify-between items-start mb-3">
                {/* Left side - Evaluation info */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {evaluationName}
                  </h3>
                  <p className="text-sm font-medium italic mb-2" style={{ color: '#247AD4' }}>
                    {roleText}
                  </p>
                </div>
                
                {/* Right side - Score */}
                <div className="text-right ml-4">
                  <span className="text-5xl font-bold" style={{ color: scoreColor }}>
                    {formatScore(totalScore)}
                  </span>
                </div>
              </div>
              
              {/* Skills tested as pills */}
              {skillsTested.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {skillsTested.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="inline-block px-3 py-1 text-sm font-medium rounded-full border"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#247AD4',
                        borderColor: '#247AD4'
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EvaluationsOverview;
